import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, RefreshCw, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ProductionLocation } from "@/hooks/useProductionLocations";
import { usePrinters, type JobType } from "@/hooks/usePrinters";

// Production-specific job types for routing
const PRODUCTION_JOB_TYPES: { value: JobType; label: string }[] = [
  { value: "kitchen", label: "Cozinha" },
  { value: "bar", label: "Bar" },
  { value: "drinks", label: "Drinks" },
  { value: "portions", label: "Porções" },
  { value: "order_production", label: "Produção Geral" },
];

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório").max(100),
  printer_id: z.string().min(1, "Impressora é obrigatória"),
  job_type: z.string().optional(),
  is_active: z.boolean(),
  sla_warning_minutes: z.coerce.number().min(1).max(120).optional(),
  sla_critical_minutes: z.coerce.number().min(1).max(240).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ProductionLocationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location: ProductionLocation | null;
  onSubmit: (data: { name: string; printer_name: string; printer_id?: string | null; job_type?: JobType | null; is_active: boolean; sla_warning_minutes?: number; sla_critical_minutes?: number }) => void;
}

export function ProductionLocationFormDialog({
  open,
  onOpenChange,
  location,
  onSubmit,
}: ProductionLocationFormDialogProps) {
  const { printers, isLoading: loadingPrinters, refetch: refetchPrinters } = usePrinters();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      printer_id: "",
      job_type: "",
      is_active: true,
      sla_warning_minutes: 10,
      sla_critical_minutes: 20,
    },
  });

  const printerId = watch("printer_id");
  const jobType = watch("job_type");
  const isActive = watch("is_active");

  useEffect(() => {
    if (open) {
      refetchPrinters();
      if (location) {
        const kdsConfig = (location as any).kds_station_config?.[0];
        reset({
          name: location.name,
          printer_id: location.printer_id || "",
          job_type: location.job_type || "",
          is_active: location.is_active,
          sla_warning_minutes: kdsConfig ? Math.round(kdsConfig.sla_warning_seconds / 60) : 10,
          sla_critical_minutes: kdsConfig ? Math.round(kdsConfig.sla_critical_seconds / 60) : 20,
        });
      } else {
        reset({
          name: "",
          printer_id: "",
          job_type: "",
          is_active: true,
          sla_warning_minutes: 10,
          sla_critical_minutes: 20,
        });
      }
    }
  }, [open, location, reset, refetchPrinters]);

  const handleFormSubmit = (data: FormData) => {
    const selectedPrinter = printers.find(p => p.id === data.printer_id);
    onSubmit({
      name: data.name,
      printer_name: selectedPrinter?.printer_name || "",
      printer_id: data.printer_id || null,
      job_type: (data.job_type as JobType) || undefined,
      is_active: data.is_active,
      sla_warning_minutes: data.sla_warning_minutes || 10,
      sla_critical_minutes: data.sla_critical_minutes || 20,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {location ? "Editar Local de Produção" : "Novo Local de Produção"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Local *</Label>
            <Input
              id="name"
              placeholder="Ex: Cozinha Principal, Bar, Copa"
              {...register("name")}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Job Type - Category for print routing */}
          <div className="space-y-2">
            <Label>Tipo de Produção</Label>
            <Select
              value={jobType}
              onValueChange={(value) => setValue("job_type", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o tipo (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTION_JOB_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Ao definir um tipo, a impressão será roteada para a impressora configurada para este tipo em Impressoras.
            </p>
          </div>

          {/* Printer Selection - from registered printers */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Impressora *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => refetchPrinters()}
                disabled={loadingPrinters}
              >
                {loadingPrinters ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="ml-1">Atualizar</span>
              </Button>
            </div>
            
            {printers.length > 0 ? (
              <Select
                value={printerId}
                onValueChange={(value) => setValue("printer_id", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma impressora cadastrada" />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((printer) => (
                    <SelectItem key={printer.id} value={printer.id}>
                      <div className="flex flex-col">
                        <span>{printer.name}</span>
                        <span className="text-xs text-muted-foreground">{printer.printer_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Nenhuma impressora cadastrada. Cadastre uma impressora em Configurações &gt; Impressoras primeiro.
                </AlertDescription>
              </Alert>
            )}
            {errors.printer_id && (
              <p className="text-sm text-destructive">
                {errors.printer_id.message}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Selecione uma impressora cadastrada no sistema. As configurações (papel, corte, etc.) serão aplicadas automaticamente.
            </p>
          </div>

          {/* SLA Config */}
          <div className="space-y-2">
            <Label>Tempo SLA do KDS</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Configure quando o timer muda de cor no KDS para esta estação.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="sla_warning" className="text-xs text-amber-500">⚠ Alerta (amarelo)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="sla_warning"
                    type="number"
                    min={1}
                    max={120}
                    {...register("sla_warning_minutes")}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="sla_critical" className="text-xs text-red-500">🔴 Crítico (vermelho)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="sla_critical"
                    type="number"
                    min={1}
                    max={240}
                    {...register("sla_critical_minutes")}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
                </div>
              </div>
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label>Ativo</Label>
              <p className="text-sm text-muted-foreground">
                Recebe pedidos do PDV
              </p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => setValue("is_active", checked)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || printers.length === 0}>
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
